import { module } from 'constant'
import { createSelector } from 'selectors'

const { hierarchy, textAnalytics, questionsOverview, wysiwygEditor, exportDefinition, datagridIconsEdit } =
	module.creator

const getHierarchySelector = () => createSelector<HTMLElement>({ selector: hierarchy.selector.context })

const getQuestionnaireSelectSelector = (scope: HTMLElement) =>
	createSelector<HTMLSelectElement>({ scope, selector: hierarchy.selector.questionnaireSelect })

const getQuestionnaireColumnsContainerSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: hierarchy.selector.questionnaireColumnsContainer })

const getHierarchyColumnsContainerSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: hierarchy.selector.hierarchyColumnsContainer })

const getHierarchyNameInputSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: hierarchy.selector.hierarchyNameInput })

const getHierarchySubmitButtonSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: hierarchy.selector.submitButton })

const getScopeSelector = () => createSelector<HTMLElement>({ selector: textAnalytics.selector.scope })

const getTextQuestionContainerSelector = (scope?: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: textAnalytics.selector.textQuestionContainer })

const getTAQuestionContainerSelector = (scope?: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: textAnalytics.selector.textAnalyticsQuestionContainer })

const getConfigKeyButtonSelector = (scope?: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: textAnalytics.selector.configKeyButton })

const getApiKeyInputSelector = (scope?: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: textAnalytics.selector.apiKeyInput })

const getTAQuestionsSelector = (scope?: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: textAnalytics.selector.taQuestion }, true)

const getTAQuestionCheckboxSelector = (scope?: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: textAnalytics.selector.checkedCheckbox })

const getTActiveSwitch = (scope?: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: textAnalytics.selector.taActiveSwitch })

const getTADomainSelect = (scope?: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: textAnalytics.selector.domainSelect })

const getTAQuestionRadiosSelector = (scope?: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: textAnalytics.selector.checkedRadioButtons })

const getWysiwygSelector = () => createSelector<HTMLElement>({ selector: wysiwygEditor.selector.own })
const getEditorWysiwygSelector = () => createSelector<HTMLElement>({ selector: wysiwygEditor.selector.editor })
const getEditorTextBlockSelector = () => createSelector<HTMLElement>({ selector: wysiwygEditor.selector.textBlock })
const getContainerWysiwygSelector = () => createSelector<HTMLDivElement>({ selector: wysiwygEditor.selector.container })
const getContentWysiwygSelector = () => createSelector<HTMLDivElement>({ selector: wysiwygEditor.selector.content })
const getWysiwygSaveButtonSelector = () =>
	createSelector<HTMLDivElement>({ selector: wysiwygEditor.selector.saveButton })

export const getTASelectors = () => {
	const scope = getScopeSelector()

	if (!scope) {
		return null
	}
	return {
		scope,
		textQuestionContainer: getTextQuestionContainerSelector(),
		domainSelect: getTADomainSelect(),
		taActiveSwitch: getTActiveSwitch(),
		taQuestionContainer: getTAQuestionContainerSelector(),
		apiKeyInput: getApiKeyInputSelector(),
		configKeyButton: getConfigKeyButtonSelector(),
	}
}

export const getTAQuestions = () => {
	const scope = getTAQuestionContainerSelector()

	if (!scope) {
		return null
	}
	return getTAQuestionsSelector(scope)
}

export const getCheckedCheckbox = (question: HTMLElement) => getTAQuestionCheckboxSelector(question)

export const getTAQuestionRadios = (question: HTMLElement) => getTAQuestionRadiosSelector(question)

export const getHierarchySelectors = () => {
	const scope = getHierarchySelector()
	if (!scope) {
		return null
	}

	return {
		questionnaireSelect: getQuestionnaireSelectSelector(scope),
		questionnaireColumnsContainer: getQuestionnaireColumnsContainerSelector(scope),
		hierarchyColumnsContainer: getHierarchyColumnsContainerSelector(scope),
		hierarchyNameInput: getHierarchyNameInputSelector(scope),
		hierarchySubmitButton: getHierarchySubmitButtonSelector(scope),
	}
}

export const getHierarchyColumns = () => {
	const scope = getHierarchySelector()
	if (!scope) {
		return null
	}
	const hierarchyColumnsContainer = getHierarchyColumnsContainerSelector(scope)
	if (!hierarchyColumnsContainer) {
		return null
	}

	return createSelector<NodeListOf<HTMLElement>>(
		{
			scope: hierarchyColumnsContainer,
			selector: hierarchy.selector.hierarchyItem,
		},
		true,
	)
}

export const getHierarchyItemWrappers = (scope: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>(
		{
			scope,
			selector: hierarchy.selector.hierarchyItemWrapper,
		},
		true,
	)

export const getItemIcon = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: hierarchy.selector.icon })

export const getHierarchyColumnsContainer = () => {
	const scope = getHierarchySelector()
	if (!scope) {
		return null
	}
	return getHierarchyColumnsContainerSelector(scope)
}

const getDatagridIconsEditContainerSelector = () =>
	createSelector<HTMLElement>({ selector: datagridIconsEdit.selector.container })

const getDatagridIconsEditIconsSelectSelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: datagridIconsEdit.selector.iconsSelect })

export const getDatagridIconsEditSelectors = () => {
	const scope = getDatagridIconsEditContainerSelector()
	if (!scope) {
		return null
	}

	return {
		container: scope,
		iconsSelect: getDatagridIconsEditIconsSelectSelector(scope),
	}
}

const getQuestionsOverviewSelector = () =>
	createSelector<HTMLElement>({ selector: questionsOverview.selector.questionsOverview })
const getQuestionsOverviewCollapsibleSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: questionsOverview.selector.questionsOverviewCollapsible })

const getQuestionsOverviewLangButtonSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: questionsOverview.selector.languageButton })

const getQuestionsOverviewExportButtonSelector = (scope: HTMLElement) =>
	createSelector<HTMLElement>({ scope, selector: questionsOverview.selector.exportButton })

export const getMetadataEditIconsSelector = (scope: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: questionsOverview.selector.metadataEdit }, true)

export const getTargetDatePickersSelector = (scope: HTMLElement, more = true) =>
	createSelector<NodeListOf<HTMLInputElement> | HTMLInputElement>(
		{ scope, selector: questionsOverview.selector.targetDatePicker },
		more,
	)

export const getTargetValueSelector = (scope: HTMLElement) =>
	createSelector<HTMLInputElement>({ scope, selector: questionsOverview.selector.targetValue })

export const getCreateTargetButtonsSelector = (scope: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: questionsOverview.selector.createTargetButton }, true)

export const getSaveTargetButtonsSelector = (scope: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: questionsOverview.selector.saveTargetButton }, true)

export const getDeleteTargetButtonsSelector = (scope: HTMLElement, more = true) =>
	createSelector<NodeListOf<HTMLElement> | HTMLElement>(
		{ scope, selector: questionsOverview.selector.deleteTargetButton },
		more,
	)

export const getQuestionsOverviewSelectors = () => {
	const scope = getQuestionsOverviewSelector()
	if (!scope) {
		return null
	}

	return {
		questionsOverviewCollapsible: getQuestionsOverviewCollapsibleSelector(scope),
		languageButton: getQuestionsOverviewLangButtonSelector(scope),
		exportButton: getQuestionsOverviewExportButtonSelector(scope),
		metadataEditIcons: getMetadataEditIconsSelector(scope),
		targetDatePickers: getTargetDatePickersSelector(scope),
		createTargetButtons: getCreateTargetButtonsSelector(scope),
		saveTargetButtons: getSaveTargetButtonsSelector(scope),
		deleteTargetButtons: getDeleteTargetButtonsSelector(scope),
	}
}

export const getValueElementOfMetadataItem = (metadataItem: HTMLElement): HTMLElement =>
	createSelector({ scope: metadataItem, selector: questionsOverview.selector.metadataValue })

export const getEditInfoElement = (collapsibleItem: HTMLElement): HTMLElement =>
	createSelector({ scope: collapsibleItem, selector: questionsOverview.selector.editInfoElement })

export const getNoTargetInfo = (targetElement: HTMLElement): HTMLElement =>
	createSelector({ scope: targetElement, selector: questionsOverview.selector.noTargetContainer })

export const getTargetEdit = (targetElement: HTMLElement): HTMLElement =>
	createSelector({ scope: targetElement, selector: questionsOverview.selector.targetEditContainer })

export const getExportDefinitionSelectors = () => {
	const scope = getExportDefinitionContainerSelector()
	if (!scope) {
		return null
	}

	return {
		container: scope,
		checkAllDisplay: getExportDefinitionCheckAllDisplaySelector(scope),
		checkAllTranslation: getExportDefinitionCheckAllTranslationSelector(scope),
		checkAllTranslationHeader: getExportDefinitionCheckAllTranslationHeaderSelector(scope),
		displayCheckboxes: getExportDefinitionDisplayCheckboxesSelector(scope),
		translateCheckboxes: getExportDefinitionTranslateCheckboxesSelector(scope),
		translateHeaderCheckboxes: getExportDefinitionTranslateHeaderCheckboxesSelector(scope),
		questionnairesSelect: getExportDefinitionQuestionnairesSelectSelector(scope),
		feedbackTableIncludedSelect: getExportDefinitionFeedbackTableIncludedSelectSelector(scope),
	}
}

const getExportDefinitionContainerSelector = () =>
	createSelector<HTMLElement>({ selector: exportDefinition.selector.container })

const getExportDefinitionCheckAllDisplaySelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: exportDefinition.selector.checkAllDisplay })

const getExportDefinitionCheckAllTranslationSelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: exportDefinition.selector.checkAllTranslation })

const getExportDefinitionCheckAllTranslationHeaderSelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: exportDefinition.selector.checkAllTranslationHeader })

const getExportDefinitionDisplayCheckboxesSelector = (scope?: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: exportDefinition.selector.displayCheckboxes }, true)

const getExportDefinitionTranslateHeaderCheckboxesSelector = (scope?: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>(
		{ scope, selector: exportDefinition.selector.translateHeaderCheckboxes },
		true,
	)

const getExportDefinitionTranslateCheckboxesSelector = (scope?: HTMLElement) =>
	createSelector<NodeListOf<HTMLElement>>({ scope, selector: exportDefinition.selector.translateCheckboxes }, true)

const getExportDefinitionQuestionnairesSelectSelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: exportDefinition.selector.questionnairesSelect })

const getExportDefinitionFeedbackTableIncludedSelectSelector = (item: HTMLElement) =>
	createSelector<HTMLElement>({ scope: item, selector: exportDefinition.selector.feedbackTableIncludedSelect })

export const getWysiwygEditorSelectors = () => ({
	wysiwyg: getWysiwygSelector(),
	editor: getEditorWysiwygSelector(),
	textBlock: getEditorTextBlockSelector(),
	container: getContainerWysiwygSelector(),
	content: getContentWysiwygSelector(),
	saveButton: getWysiwygSaveButtonSelector(),
})
